import type { KeyEasing } from '../anim-track';

const remapEasing = (t: number, easing: KeyEasing | undefined): number => {
    if (!easing) return t;
    switch (easing) {
        case 'linear': return t;
        case 'easeIn': return t * t;
        case 'easeOut': return 1 - (1 - t) * (1 - t);
        case 'easeInOut': return t * t * (3 - 2 * t);
        case 'step': return t < 1 ? 0 : 1;
        default: return t;
    }
};

class CubicSpline {
    // control times
    times: number[];

    // control data: in-tangent, point, out-tangent
    knots: number[];

    // dimension of the knot points
    dim: number;

    // optional per-segment easing (indexed by segment, i.e. by the starting keyframe)
    easings: (KeyEasing | undefined)[] | null;

    constructor(times: number[], knots: number[], easings?: (KeyEasing | undefined)[] | null) {
        this.times = times;
        this.knots = knots;
        this.dim = knots.length / times.length / 3;
        this.easings = easings ?? null;
    }

    evaluate(time: number, result: number[]) {
        const { times, easings } = this;
        const last = times.length - 1;

        if (time <= times[0]) {
            this.getKnot(0, result);
        } else if (time >= times[last]) {
            this.getKnot(last, result);
        } else {
            let seg = 0;
            while (time >= times[seg + 1]) {
                seg++;
            }
            let t = (time - times[seg]) / (times[seg + 1] - times[seg]);
            if (easings && seg < easings.length) {
                t = remapEasing(t, easings[seg]);
            }
            this.evaluateSegment(seg, t, result);
        }
    }

    getKnot(index: number, result: number[]) {
        const { knots, dim } = this;
        const idx = index * 3 * dim;
        for (let i = 0; i < dim; ++i) {
            result[i] = knots[idx + i * 3 + 1];
        }
    }

    // evaluate the spline segment at the given normalized time t
    evaluateSegment(segment: number, t: number, result: number[]) {
        const { knots, dim } = this;

        const t2 = t * t;
        const twot = t + t;
        const omt = 1 - t;
        const omt2 = omt * omt;

        let idx = segment * dim * 3;                    // each knot has 3 values: tangent in, value, tangent out
        for (let i = 0; i < dim; ++i) {
            const p0 = knots[idx + 1];                  // p0
            const m0 = knots[idx + 2];                  // outgoing tangent
            const m1 = knots[idx + dim * 3];            // incoming tangent
            const p1 = knots[idx + dim * 3 + 1];        // p1
            idx += 3;

            result[i] =
                p0 * ((1 + twot) * omt2) +
                m0 * (t * omt2) +
                p1 * (t2 * (3 - twot)) +
                m1 * (t2 * (t - 1));
        }
    }

    // calculate cubic spline knots from points
    // times: time values for each control point
    // points: control point values to be interpolated (n dimensional)
    // smoothness: 0 = linear, 1 = smooth
    static calcKnots(times: number[], points: number[], smoothness: number) {
        const n = times.length;
        const dim = points.length / n;
        const knots = new Array<number>(n * dim * 3);

        for (let i = 0; i < n; i++) {
            const t = times[i];

            for (let j = 0; j < dim; j++) {
                const idx = i * dim + j;
                const p = points[idx];

                let tangent;
                if (i === 0) {
                    tangent = (points[idx + dim] - p) / (times[i + 1] - t);
                } else if (i === n - 1) {
                    tangent = (p - points[idx - dim]) / (t - times[i - 1]);
                } else {
                    tangent = (points[idx + dim] - points[idx - dim]) / (times[i + 1] - times[i - 1]);
                }

                // convert to derivatives w.r.t normalized segment parameter
                const inScale = i > 0 ? (times[i] - times[i - 1]) : (times[1] - times[0]);
                const outScale = i < n - 1 ? (times[i + 1] - times[i]) : (times[i] - times[i - 1]);

                knots[idx * 3] = tangent * inScale * smoothness;
                knots[idx * 3 + 1] = p;
                knots[idx * 3 + 2] = tangent * outScale * smoothness;
            }
        }

        return knots;
    }

    static fromPoints(times: number[], points: number[], smoothness = 1, easings?: (KeyEasing | undefined)[] | null) {
        return new CubicSpline(times, CubicSpline.calcKnots(times, points, smoothness), easings);
    }

    // create a looping spline by duplicating animation points at the end and beginning
    static fromPointsLooping(length: number, times: number[], points: number[], smoothness = 1, easings?: (KeyEasing | undefined)[] | null) {
        if (times.length < 2) {
            return CubicSpline.fromPoints(times, points, smoothness, easings);
        }

        const dim = points.length / times.length;
        const newTimes = times.slice();
        const newPoints = points.slice();

        // append first two points
        newTimes.push(length + times[0], length + times[1]);
        newPoints.push(...points.slice(0, dim * 2));

        // prepend last two points
        newTimes.splice(0, 0, times[times.length - 2] - length, times[times.length - 1] - length);
        newPoints.splice(0, 0, ...points.slice(points.length - dim * 2));

        // Extend easings array to match: 2 prepended + original + 2 appended
        let newEasings: (KeyEasing | undefined)[] | null = null;
        if (easings) {
            newEasings = [];
            // prepended segments use easing from the last two original keys
            const n = easings.length;
            newEasings.push(n >= 2 ? easings[n - 2] : undefined);
            newEasings.push(n >= 1 ? easings[n - 1] : undefined);
            // original easings
            newEasings.push(...easings);
            // appended segments use easing from the first two original keys
            newEasings.push(easings[0] ?? undefined);
            newEasings.push(easings.length > 1 ? easings[1] : undefined);
        }

        return CubicSpline.fromPoints(newTimes, newPoints, smoothness, newEasings);
    }
}

export { CubicSpline };
